import {
  ampathSystem,
  createCodeableConcept,
  createFhirDateTime,
  createIdentifier,
  DiagnosticReportIdentifier,
  getIdentifier,
  IdentifierCode,
  isValidReference,
  ServiceRequestCode,
  ServiceRequestPerformerIdentifier,
  system,
} from '@quromedical/fhir-common';
import { compact, first } from '@quromedical/utils';
import type { WithId } from '../../types';

import type { PathologyReport, PathologyReportItem } from '../schema';

export const mapModelToFhirDiagnosticReport = (
  pathologyReport: PathologyReport,
): fhir4.DiagnosticReport => {
  const { patient, status, orderNumber, issued, order, reports } =
    pathologyReport;

  const presentedForm = reports.map<fhir4.Attachment>((report) => ({
    contentType: report.contentType,
    hash: report.hash,
    title: report.title,
    url: report.url,
  }));

  return {
    resourceType: 'DiagnosticReport',
    subject: patient,
    basedOn: order ? [order] : undefined,
    effectiveDateTime: createFhirDateTime(new Date(issued)),
    code: createCodeableConcept(
      ServiceRequestCode.PathologyRequest,
      ServiceRequestCode.PathologyRequest,
      ServiceRequestCode.PathologyRequest,
      system,
    ),
    performer: [
      {
        identifier: createIdentifier(
          IdentifierCode.serviceRequestPerformer,
          ServiceRequestPerformerIdentifier.Ampath,
          ampathSystem,
        ),
      },
    ],
    status,
    identifier: orderNumber
      ? [
          createIdentifier(
            DiagnosticReportIdentifier.AmpathOrderNumber,
            orderNumber,
            ampathSystem,
          ),
        ]
      : undefined,
    presentedForm,
  };
};

export const mapFhirDiagnosticReportToModel = (
  fhir: WithId<fhir4.DiagnosticReport>,
): WithId<Partial<PathologyReport>> => {
  const {
    id,
    subject,
    status,
    effectiveDateTime,
    identifier,
    presentedForm,
    performer,
    basedOn,
  } = fhir;

  const reports = presentedForm?.map((form) => {
    if (!form.contentType || !form.hash || !form.title || !form.url) {
      return undefined;
    }

    const report: PathologyReportItem = {
      contentType: 'application/pdf',
      hash: form.hash,
      title: form.title,
      url: form.url,
    };

    return report;
  });

  const ampathOrderNo = getIdentifier(
    DiagnosticReportIdentifier.AmpathOrderNumber,
    identifier,
  );
  const lancetOrderNo = getIdentifier(
    DiagnosticReportIdentifier.LancetOrderNumber,
    identifier,
  );

  const orderNumber =
    ampathOrderNo == undefined ? lancetOrderNo : ampathOrderNo;

  const serviceRequest = first(basedOn);

  return {
    id,
    status,
    order: isValidReference(serviceRequest) ? serviceRequest : undefined,
    issued: effectiveDateTime ? effectiveDateTime : undefined,
    orderNumber,
    performer: first(performer)?.identifier
      ? ServiceRequestPerformerIdentifier.Ampath
      : undefined,
    patient: isValidReference(subject) ? subject : undefined,
    reports: compact(reports),
  };
};
